/**
 * GroupContextHeader — sticky, contextual header inside a group page.
 * Replaces the global app header (which is hidden on `/groups/:id` routes via Layout.js).
 *
 * Surfaces the group name + member count + admin badge, plus three primary
 * affordances: back, AI assistant (slides up a chat panel), and admin overflow.
 *
 * Spec: design_guidelines.json → component_specs.group_header
 *   sticky top-0 z-50 backdrop-blur-xl border-b
 */
import { useNavigate } from 'react-router-dom';
import GroupLogo from './GroupLogo';
import { ChevronLeft, Sparkles, MoreVertical, ShieldCheck, UserPlus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import NeoPayProofLink from './NeoPayProofLink';

export default function GroupContextHeader({ group, isAdmin, onOpenAi, onOpenMenu, onInvite, liveDot = false }) {
  const navigate = useNavigate();
  const memberCount = group?.member_ids?.length || 0;

  return (
    <header
      data-testid="group-context-header"
      className="sticky top-0 z-50 -mx-4 px-4 py-3 mb-4 backdrop-blur-xl bg-background/85 border-b border-border/60 flex items-center gap-2"
    >
      <button
        onClick={() => navigate('/groups')}
        data-testid="group-back-btn"
        aria-label="Back to groups"
        className="w-9 h-9 -ml-1 rounded-full flex items-center justify-center text-foreground/80 hover:bg-accent hover:text-foreground active:scale-95 transition"
      >
        <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <h1
            className="text-base font-bold tracking-tight truncate"
            data-testid="group-context-name"
          >
            {group?.logo_url
              ? <GroupLogo logoUrl={group.logo_url} size="sm" className="inline-block align-[-5px] mr-1.5" testId="group-context-logo" />
              : group?.emoji && <span className="mr-1" data-testid="group-context-emoji">{group.emoji}</span>}
            {group?.name || 'Group'}
          </h1>
          {isAdmin && (
            <span
              className="inline-flex items-center gap-0.5 text-xs font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-primary/10 text-primary"
              data-testid="group-context-admin-pill"
              title="You're an admin of this group"
            >
              <ShieldCheck className="w-2.5 h-2.5" /> Admin
            </span>
          )}
          {/* iter131 — group genesis proof link */}
          <NeoPayProofLink
            url={group?.neopay_genesis_proof_url}
            testId="neopay-proof-group-genesis"
            label="View group genesis on NeoPay ledger"
            size="xs"
          />
        </div>
        <p className="text-xs text-muted-foreground tracking-wide truncate">
          {memberCount} {memberCount === 1 ? 'member' : 'members'}
          {group?.sport && group?._sport_meta && ` · ${group._sport_meta.emoji} ${group._sport_meta.label}`}
          {!group?.sport && group?.group_type && ` · ${group.group_type}`}
        </p>
      </div>

      <button
        onClick={onOpenAi}
        data-testid="group-ai-btn"
        aria-label="AI assistant"
        title="AI assistant"
        className="relative w-9 h-9 rounded-full flex items-center justify-center text-foreground/80 hover:bg-accent hover:text-primary active:scale-95 transition"
      >
        <Sparkles className="w-5 h-5" strokeWidth={2.25} />
        <AnimatePresence>
          {liveDot && (
            <motion.span
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-primary ring-2 ring-background"
            />
          )}
        </AnimatePresence>
      </button>

      {/* iter127 — prominent "+ Invite" pill (always visible to every member) */}
      {onInvite && (
        <button
          onClick={onInvite}
          data-testid="group-invite-pill"
          aria-label="Share invite link"
          title="Share invite link"
          className="inline-flex items-center gap-1 h-9 px-2.5 rounded-full bg-primary text-primary-foreground font-bold text-xs uppercase tracking-wider active:scale-95 transition shadow-sm hover:shadow-md"
        >
          <UserPlus className="w-3.5 h-3.5" strokeWidth={2.6} />
          <span className="hidden sm:inline">Invite</span>
        </button>
      )}

      <button
        onClick={onOpenMenu}
        data-testid="group-menu-btn"
        aria-label="Group menu"
        className="w-9 h-9 -mr-1 rounded-full flex items-center justify-center text-foreground/80 hover:bg-accent hover:text-foreground active:scale-95 transition"
      >
        <MoreVertical className="w-5 h-5" strokeWidth={2.25} />
      </button>
    </header>
  );
}
